// CRM-LEADS-2026-10-16 §5: Web Push (VAPID) on the installed CRM -- opt in per device from Settings. The browser's push
// subscription (endpoint + two public keys) is saved to crm_push_subscriptions (migrations/2026-10-16-crm-push-subscriptions.sql,
// RLS: your own devices only); the agents window's notification path sends to every active one (sites tools/reports/
// CRM-LEADS-2026-10-16.md §5 has their contract). A tap on a notification opens that lead with the compose box ready.
//
// The VAPID PUBLIC key is public by design (it is in every subscription request). Its private half is a secret named
// VAPID_PRIVATE_KEY, set on the sender only; it is never in this repo.
// PUSH-2026-10-22: a FRESH pair (the old private half was lost). recruiter-agent's VAPID_PUBLIC_KEY var must be this same
// value. A device subscribed with the old key cannot receive pushes signed with the new one, so a subscription made with
// any other key is replaced (unsubscribed, its row removed, subscribed again) the next time Settings opens or on Turn on.
export const VAPID_PUBLIC_KEY = 'BFUMSrKJ2TK-GN-Wmipq68AkKTqQ9448Y7YNtN3RSMngRvXv39y2Pbb68-GVvAam_J_DRBA0SuDe-B_gNOMkwYc';
export const PUSH_TABLE = 'crm_push_subscriptions';

export function urlBase64ToUint8Array(s) {
  const pad = '='.repeat((4 - (s.length % 4)) % 4);
  const b64 = (s + pad).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}
const b64u = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

/** 'unsupported' (no service worker / push / notifications) | 'blocked' (permission denied) | 'ok' */
export function pushSupport(win = typeof window !== 'undefined' ? window : null) {
  if (!win || !win.navigator || !('serviceWorker' in win.navigator) || !win.PushManager || !win.Notification) return 'unsupported';
  return win.Notification.permission === 'denied' ? 'blocked' : 'ok';
}

/** A browser subscription -> the row we keep (no phone, no message: the device's address for pushes only). */
export function subscriptionRow(sub, user, ua = '') {
  const j = typeof sub.toJSON === 'function' ? sub.toJSON() : sub;
  const keys = j.keys || {};
  return { user_id: user.id, user_email: user.email || null, endpoint: j.endpoint, p256dh: keys.p256dh, auth: keys.auth,
    user_agent: String(ua || '').slice(0, 300), last_seen_at: new Date().toISOString(), disabled_at: null };
}

const missing = (e) => !!e && (/PGRST205|42P01/.test(String(e.code || '')) || /does not exist|could not find the table/i.test(String(e.message || '')));

// PUSH-2026-10-22: why Matt's Android device showed "On" with 0 rows -- the subscribe ran BEFORE the save, and "On" was read
// from the browser's subscription alone. A first save that failed (the table not there yet, a refusal) left a subscription
// with no row, and every later visit said "On" without saving it. Now "On" means the row is there; a subscription without
// its row is saved again when Settings opens; the save is read back; and every failure is said on screen.
export const PUSH_ERRORS = {
  no_worker: 'The app\'s background worker is not running, so notifications cannot be set up. Close the app completely and open it again.',
  not_saved: 'This device subscribed, but the CRM could not save it. Nothing will arrive until it is saved: try Turn on again.',
  signed_out: 'Sign in again to turn on notifications.',
  pending: 'Notifications are not switched on for the CRM yet (the database update is pending).',
};
const sameKey = (sub) => {
  const k = sub && sub.options && sub.options.applicationServerKey;
  if (!k) return true;                                    // the browser does not say: keep it
  const a = new Uint8Array(k), b = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
  return a.length === b.length && a.every((x, i) => x === b[i]);
};
const withTimeout = (p, ms) => Promise.race([p, new Promise((res) => setTimeout(() => res(null), ms))]);

export function makePushApi({ supabase, win = typeof window !== 'undefined' ? window : null, workerWaitMs = 5000 }) {
  const reg = async () => {
    const sw = win.navigator.serviceWorker;
    const r = (sw.getRegistration ? await sw.getRegistration() : null) || await withTimeout(sw.ready, workerWaitMs);
    if (!r || !r.pushManager) { const e = new Error(PUSH_ERRORS.no_worker); e.code = 'no_worker'; throw e; }
    return r;
  };
  const rowFor = async (endpoint) => {
    const { data, error } = await supabase.from(PUSH_TABLE).select('id').eq('endpoint', endpoint).limit(1);
    if (error) throw new Error(error.message);
    return (data || [])[0] || null;
  };
  const save = async (sub) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { const e = new Error(PUSH_ERRORS.signed_out); e.code = 'signed_out'; throw e; }
    const { error } = await supabase.from(PUSH_TABLE).upsert(subscriptionRow(sub, user, win.navigator.userAgent), { onConflict: 'endpoint' });
    if (error) throw new Error(missing(error) ? PUSH_ERRORS.pending : `${PUSH_ERRORS.not_saved} (${error.message})`);
    if (!(await rowFor(sub.endpoint))) { const e = new Error(PUSH_ERRORS.not_saved); e.code = 'not_saved'; throw e; }   // read back: RLS can drop a write silently
  };
  // the browser's subscription, made with today's key; one made with another key is replaced (and its row removed)
  const current = async (r, { create }) => {
    let sub = await r.pushManager.getSubscription();
    if (sub && !sameKey(sub)) {
      const old = sub.endpoint;
      await sub.unsubscribe().catch(() => {});
      await supabase.from(PUSH_TABLE).delete().eq('endpoint', old);
      sub = null;
    }
    if (!sub && create) sub = await r.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) });
    return sub;
  };
  return {
    /** -> { ready:false } before the migration; { ready:true, on, endpoint, error? } after. "on" = the row is saved. */
    async status() {
      const { error } = await supabase.from(PUSH_TABLE).select('id').limit(1);
      if (error) return missing(error) ? { ready: false } : { ready: true, on: false, error: error.message };
      if (pushSupport(win) !== 'ok') return { ready: true, on: false };
      try {
        const r = await reg();
        const sub = await current(r, { create: false });
        if (!sub) return { ready: true, on: false, endpoint: null };
        if (!(await rowFor(sub.endpoint))) await save(sub);     // subscribed but never saved: save it now (Matt's case)
        return { ready: true, on: true, endpoint: sub.endpoint, repaired: true };
      } catch (e) {
        return { ready: true, on: false, error: e.message || PUSH_ERRORS.not_saved };
      }
    },
    /** Ask permission, subscribe this device with today's key, save it, read it back. -> 'on' | 'blocked' | 'unsupported' | throws */
    async enable() {
      if (pushSupport(win) === 'unsupported') return 'unsupported';
      const perm = await win.Notification.requestPermission();
      if (perm !== 'granted') return 'blocked';
      const r = await reg();
      const sub = await current(r, { create: true });
      await save(sub);
      return 'on';
    },
    /** Unsubscribe this device and forget it. */
    async disable() {
      const r = await reg();
      const sub = r && r.pushManager ? await r.pushManager.getSubscription() : null;
      if (!sub) return 'off';
      const endpoint = sub.endpoint;
      await sub.unsubscribe().catch(() => {});
      const { error } = await supabase.from(PUSH_TABLE).delete().eq('endpoint', endpoint);
      if (error && !missing(error)) throw new Error(error.message);
      return 'off';
    },
  };
}
export { b64u as _b64u };
